import { getVideoApiBase } from './translatorHandoff';
import { decodeJoinToken, getJoinTokenFromUrl } from './videoJoinToken';
import { extractAppointmentIdFromRoom } from './returnUrl';

export interface AppointmentReport {
  id?: number;
  appointmentId?: number;
  content?: string;
}

export function resolveAppointmentId(roomName?: string | null): number | null {
  const fromLiveRoom = extractAppointmentIdFromRoom(roomName);
  if (fromLiveRoom) {
    return Number(fromLiveRoom);
  }
  const token = getJoinTokenFromUrl();
  const tokenRoomId = token ? decodeJoinToken(token)?.roomId : null;
  const queryRoomId = new URLSearchParams(window.location.search).get('r');
  const fromToken = extractAppointmentIdFromRoom(tokenRoomId);
  if (fromToken) {
    return Number(fromToken);
  }
  const fromQuery = extractAppointmentIdFromRoom(queryRoomId);
  if (fromQuery) {
    return Number(fromQuery);
  }
  const fallback = (roomName || tokenRoomId || queryRoomId || '').match(/(\d+)(?!.*\d)/);
  if (!fallback) {
    return null;
  }
  const id = Number(fallback[1]);
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function resolveReportLanguage(): string {
  const params = new URLSearchParams(window.location.search);
  const raw = (params.get('lang') || params.get('language') || '').trim().toLowerCase();
  if (raw.startsWith('en')) {
    return 'en';
  }
  if (raw.startsWith('ar')) {
    return 'ar';
  }
  return 'ar';
}

export async function generateNotaReportFromAudio(
  audioBlob: Blob,
  roomName?: string | null
): Promise<AppointmentReport> {
  const appointmentId = resolveAppointmentId(roomName);
  if (!appointmentId) {
    throw new Error('Could not determine appointment id from the video room.');
  }

  const apiBase = getVideoApiBase();
  const formData = new FormData();
  formData.append('audio', audioBlob, 'consultation.wav');
  formData.append('appointmentId', String(appointmentId));
  formData.append('language', resolveReportLanguage());

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
  const timeout = controller ? window.setTimeout(() => controller.abort(), 130000) : undefined;

  try {
    const response = await fetch(`${apiBase}/reports/generateFromAudio`, {
      method: 'POST',
      body: formData,
      signal: controller ? controller.signal : undefined,
    });
    const rawText = await response.text();
    let payload: any = {};
    try {
      payload = rawText ? JSON.parse(rawText) : {};
    } catch {
      payload = { message: rawText };
    }

    if (!response.ok) {
      const message = payload?.error || payload?.message || `Failed to generate medical report (${response.status})`;
      throw new Error(message);
    }
    return payload;
  } catch (err) {
    if (err && (err as Error).name === 'AbortError') {
      throw new Error('Medical report request timed out. Please try again from the appointment page.');
    }
    throw err;
  } finally {
    if (timeout) {
      window.clearTimeout(timeout);
    }
  }
}
